'use client';

import { useState } from 'react';
import ToolHero from '@/components/tools/ToolHero';
import ToolCTA from '@/components/tools/ToolCTA';
import CopyButton from '@/components/tools/CopyButton';
import {
  RefreshCw,
  Users,
  Target,
  Clock,
  Heart,
  Lightbulb,
  AlertCircle,
  MessageSquare,
} from 'lucide-react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type Purpose =
  | 'Networking'
  | 'Job Interest'
  | 'Industry Advice'
  | 'Mutual Connection'
  | 'Admired Their Work'
  | 'Alumni';

interface GeneratedMessage {
  text: string;
  id: number;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const CHAR_LIMIT = 300;

const purposes: Purpose[] = [
  'Networking',
  'Job Interest',
  'Industry Advice',
  'Mutual Connection',
  'Admired Their Work',
  'Alumni',
];

// ---------------------------------------------------------------------------
// Utility
// ---------------------------------------------------------------------------

function pickRandom<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

function trimToLimit(text: string, limit: number): string {
  if (text.length <= limit) return text;
  // Try to cut at last space before limit
  const trimmed = text.slice(0, limit);
  const lastSpace = trimmed.lastIndexOf(' ');
  return lastSpace > limit * 0.7 ? trimmed.slice(0, lastSpace) : trimmed;
}

// ---------------------------------------------------------------------------
// Message generation engine
// ---------------------------------------------------------------------------

// Greeting variations
const greetings = [
  (name: string) => `Hi ${name},`,
  (name: string) => `Hello ${name},`,
  (name: string) => `Hi ${name} --`,
  (name: string) => `${name},`,
  (name: string) => `Hey ${name},`,
];

// Closings
const closings = [
  'Would love to connect!',
  'Hope to connect!',
  'Looking forward to connecting.',
  "Let's connect!",
  'Would be great to connect.',
  'Happy to chat anytime.',
  'Would welcome the chance to connect.',
];

// Purpose-specific openers and body phrases
const purposeBank: Record<Purpose, {
  openers: string[];
  bodies: ((role: string, company: string, yourRole: string, detail: string) => string)[];
}> = {
  'Networking': {
    openers: [
      'I came across your profile and was impressed by your work',
      'Your background caught my eye',
      'I noticed we work in similar spaces',
      'I have been following your career with interest',
      'Your experience in the field is inspiring',
    ],
    bodies: [
      (role, company, yourRole, detail) =>
        `as ${role} at ${company}. I am ${yourRole && `a ${yourRole}`}${detail ? ` and ${detail}` : ''}. Always great to connect with fellow professionals in the space.`,
      (role, company, yourRole, detail) =>
        `at ${company}. As ${yourRole ? `a ${yourRole}` : 'someone in the field'}, I am always looking to expand my network with talented professionals like yourself.${detail ? ` ${detail}.` : ''}`,
      (role, company, yourRole, detail) =>
        `and would love to add you to my professional network.${yourRole ? ` I am a ${yourRole}` : ''}${detail ? ` and ${detail}` : ''}.`,
      (role, company, yourRole, detail) =>
        `at ${company}. ${yourRole ? `As a fellow ${yourRole}, ` : ''}I think we could have some great conversations.${detail ? ` ${detail}.` : ''}`,
      (role, company, yourRole, detail) =>
        `-- particularly your work${company ? ` at ${company}` : ''}.${yourRole ? ` I am a ${yourRole}` : ''} and would value having you in my network.${detail ? ` ${detail}.` : ''}`,
    ],
  },
  'Job Interest': {
    openers: [
      `I am very interested in opportunities at`,
      `I have been researching`,
      `I am exploring roles at`,
      `I am drawn to the work being done at`,
      `I admire the mission of`,
    ],
    bodies: [
      (role, company, yourRole, detail) =>
        `${company} and your role as ${role} caught my attention.${yourRole ? ` I am a ${yourRole}` : ''} and would love to learn more about your team.${detail ? ` ${detail}.` : ''}`,
      (role, company, yourRole, detail) =>
        `${company} and would love to connect. ${yourRole ? `As a ${yourRole}, ` : ''}I think my background could be a great fit.${detail ? ` ${detail}.` : ''}`,
      (role, company, yourRole, detail) =>
        `${company}.${yourRole ? ` I am a ${yourRole} and` : ''} I would appreciate any insight into what it is like working there.${detail ? ` ${detail}.` : ''}`,
      (role, company, yourRole, detail) =>
        `${company} and noticed your background as ${role}. ${yourRole ? `I am a ${yourRole}. ` : ''}Would love to hear about your experience.${detail ? ` ${detail}.` : ''}`,
      (role, company, yourRole, detail) =>
        `${company}. Your work as ${role} is impressive.${yourRole ? ` I am a ${yourRole}` : ''} exploring opportunities in this space.${detail ? ` ${detail}.` : ''}`,
    ],
  },
  'Industry Advice': {
    openers: [
      'Your expertise as',
      'I have been following your insights as',
      'Your experience as',
      'I am impressed by your journey as',
      'Your perspective as',
    ],
    bodies: [
      (role, company, yourRole, detail) =>
        `${role}${company ? ` at ${company}` : ''} is exactly the kind of insight I am looking for.${yourRole ? ` I am a ${yourRole}` : ''} and would value your perspective on the industry.${detail ? ` ${detail}.` : ''}`,
      (role, company, yourRole, detail) =>
        `${role} is something I deeply respect.${yourRole ? ` As a ${yourRole}, ` : ''} I would love to learn from your experience${company ? ` at ${company}` : ''}.${detail ? ` ${detail}.` : ''}`,
      (role, company, yourRole, detail) =>
        `${role}${company ? ` at ${company}` : ''} would be incredibly valuable.${yourRole ? ` I am a ${yourRole}` : ''} navigating some big decisions and would appreciate any guidance.${detail ? ` ${detail}.` : ''}`,
      (role, company, yourRole, detail) =>
        `${role} stands out.${yourRole ? ` I am a ${yourRole} and` : ''} I have a few questions about the industry${detail ? ` -- ${detail}` : ''}. Would love to pick your brain.`,
      (role, company, yourRole, detail) =>
        `${role}${company ? ` at ${company}` : ''} resonates with me.${yourRole ? ` As a ${yourRole}, ` : ' '}I am always looking to learn from experienced professionals.${detail ? ` ${detail}.` : ''}`,
    ],
  },
  'Mutual Connection': {
    openers: [
      'I noticed we both know',
      'I see we have mutual connections and',
      'We share some connections and',
      'Our shared network suggests we should connect --',
      'A mutual contact mentioned your work and',
    ],
    bodies: [
      (role, company, yourRole, detail) =>
        `I thought it would be great to connect directly. Your work as ${role}${company ? ` at ${company}` : ''} is impressive.${yourRole ? ` I am a ${yourRole}.` : ''}${detail ? ` ${detail}.` : ''}`,
      (role, company, yourRole, detail) =>
        `I wanted to reach out. ${yourRole ? `As a fellow ${yourRole}, ` : ''}I think we could have some valuable exchanges.${detail ? ` ${detail}.` : ''}`,
      (role, company, yourRole, detail) =>
        `I would love to expand our shared network.${yourRole ? ` I am a ${yourRole}` : ''} and your experience as ${role}${company ? ` at ${company}` : ''} is really compelling.${detail ? ` ${detail}.` : ''}`,
      (role, company, yourRole, detail) =>
        `I am reaching out because our networks overlap.${yourRole ? ` I am a ${yourRole} and` : ''} I think connecting could be mutually beneficial.${detail ? ` ${detail}.` : ''}`,
      (role, company, yourRole, detail) =>
        `I think we would benefit from being connected. Your ${role} experience${company ? ` at ${company}` : ''} aligns well with my interests${yourRole ? ` as a ${yourRole}` : ''}.${detail ? ` ${detail}.` : ''}`,
    ],
  },
  'Admired Their Work': {
    openers: [
      'I have been following your work and',
      'Your recent work really caught my attention --',
      'I am genuinely impressed by what you have accomplished',
      'Your contributions to the field are outstanding --',
      'I have been admiring your work',
    ],
    bodies: [
      (role, company, yourRole, detail) =>
        `particularly your impact as ${role}${company ? ` at ${company}` : ''}. ${yourRole ? `As a ${yourRole}, ` : ''}I find your approach truly inspiring.${detail ? ` ${detail}.` : ''}`,
      (role, company, yourRole, detail) =>
        `especially in your role${company ? ` at ${company}` : ''}.${yourRole ? ` I am a ${yourRole} and` : ''} your work has influenced my own thinking.${detail ? ` ${detail}.` : ''}`,
      (role, company, yourRole, detail) =>
        `as ${role}${company ? ` at ${company}` : ''} and wanted to connect.${yourRole ? ` I am a ${yourRole}.` : ''}${detail ? ` ${detail}.` : ''} Your work is truly motivating.`,
      (role, company, yourRole, detail) =>
        `and it has had a real impact on how I approach my own work.${yourRole ? ` As a ${yourRole}, ` : ' '}I would be honored to connect.${detail ? ` ${detail}.` : ''}`,
      (role, company, yourRole, detail) =>
        `${company ? `at ${company} ` : ''}and I am continually impressed.${yourRole ? ` I am a ${yourRole}` : ''} who looks up to professionals like you.${detail ? ` ${detail}.` : ''}`,
    ],
  },
  'Alumni': {
    openers: [
      'Fellow alum here!',
      'I see we share the same alma mater --',
      'Always great to meet a fellow alum!',
      'Our shared educational background caught my eye --',
      'As a fellow graduate,',
    ],
    bodies: [
      (role, company, yourRole, detail) =>
        `Your path as ${role}${company ? ` at ${company}` : ''} is really impressive.${yourRole ? ` I am a ${yourRole}` : ''} and would love to connect with a fellow graduate.${detail ? ` ${detail}.` : ''}`,
      (role, company, yourRole, detail) =>
        `I love seeing alumni doing great things${company ? ` at ${company}` : ''}.${yourRole ? ` As a ${yourRole}, ` : ' '}I think our shared background makes this a natural connection.${detail ? ` ${detail}.` : ''}`,
      (role, company, yourRole, detail) =>
        `I thought it would be great to connect.${yourRole ? ` I am a ${yourRole}` : ''} and always enjoy networking with people who share my educational background.${detail ? ` ${detail}.` : ''}`,
      (role, company, yourRole, detail) =>
        `Your career as ${role}${company ? ` at ${company}` : ''} is exactly the type of path I find inspiring.${yourRole ? ` I am currently a ${yourRole}.` : ''}${detail ? ` ${detail}.` : ''}`,
      (role, company, yourRole, detail) =>
        `I am reaching out to build my alumni network.${yourRole ? ` I am a ${yourRole} and` : ''} your work${company ? ` at ${company}` : ''} caught my attention.${detail ? ` ${detail}.` : ''}`,
    ],
  },
};

function generateMessages(
  theirName: string,
  theirRole: string,
  theirCompany: string,
  purpose: Purpose,
  yourRole: string,
  personalDetail: string,
): GeneratedMessage[] {
  const name = theirName || 'there';
  const role = theirRole || 'professional';
  const company = theirCompany || '';
  const detail = personalDetail.trim();

  const bank = purposeBank[purpose];
  const messages: GeneratedMessage[] = [];
  const usedTexts = new Set<string>();

  // Generate 5 unique messages
  let attempts = 0;
  while (messages.length < 5 && attempts < 50) {
    attempts++;

    const greetFn = pickRandom(greetings);
    const greet = greetFn(name);
    const opener = pickRandom(bank.openers);
    const bodyFn = pickRandom(bank.bodies);
    const body = bodyFn(role, company, yourRole, detail);
    const closing = pickRandom(closings);

    let fullMessage = `${greet} ${opener} ${body} ${closing}`;

    // Trim to fit within the character limit
    fullMessage = trimToLimit(fullMessage, CHAR_LIMIT);

    // Skip duplicates
    if (usedTexts.has(fullMessage)) continue;
    usedTexts.add(fullMessage);

    messages.push({
      text: fullMessage,
      id: Date.now() + messages.length,
    });
  }

  return messages;
}

function regenerateSingleMessage(
  index: number,
  theirName: string,
  theirRole: string,
  theirCompany: string,
  purpose: Purpose,
  yourRole: string,
  personalDetail: string,
  existingTexts: string[],
): GeneratedMessage {
  const name = theirName || 'there';
  const role = theirRole || 'professional';
  const company = theirCompany || '';
  const detail = personalDetail.trim();

  const bank = purposeBank[purpose];

  let attempts = 0;
  while (attempts < 30) {
    attempts++;

    const greetFn = pickRandom(greetings);
    const greet = greetFn(name);
    const opener = pickRandom(bank.openers);
    const bodyFn = pickRandom(bank.bodies);
    const body = bodyFn(role, company, yourRole, detail);
    const closing = pickRandom(closings);

    let fullMessage = `${greet} ${opener} ${body} ${closing}`;
    fullMessage = trimToLimit(fullMessage, CHAR_LIMIT);

    if (!existingTexts.includes(fullMessage)) {
      return { text: fullMessage, id: Date.now() };
    }
  }

  // Fallback: return a new message even if duplicate
  const greetFn = pickRandom(greetings);
  const opener = pickRandom(bank.openers);
  const bodyFn = pickRandom(bank.bodies);
  const body = bodyFn(role, company, yourRole, detail);
  const closing = pickRandom(closings);
  let fallback = `${greetFn(name)} ${opener} ${body} ${closing}`;
  fallback = trimToLimit(fallback, CHAR_LIMIT);

  return { text: fallback, id: Date.now() };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function ConnectionRequestContent() {
  const [theirName, setTheirName] = useState('');
  const [theirRole, setTheirRole] = useState('');
  const [theirCompany, setTheirCompany] = useState('');
  const [purpose, setPurpose] = useState<Purpose>('Networking');
  const [yourRole, setYourRole] = useState('');
  const [personalDetail, setPersonalDetail] = useState('');
  const [messages, setMessages] = useState<GeneratedMessage[]>([]);
  const [hasGenerated, setHasGenerated] = useState(false);

  const handleGenerate = (e: React.FormEvent) => {
    e.preventDefault();
    if (!theirName.trim() && !theirRole.trim()) return;
    const result = generateMessages(
      theirName.trim(),
      theirRole.trim(),
      theirCompany.trim(),
      purpose,
      yourRole.trim(),
      personalDetail.trim(),
    );
    setMessages(result);
    setHasGenerated(true);
  };

  const handleRegenerateAll = () => {
    const result = generateMessages(
      theirName.trim(),
      theirRole.trim(),
      theirCompany.trim(),
      purpose,
      yourRole.trim(),
      personalDetail.trim(),
    );
    setMessages(result);
  };

  const handleRegenerateSingle = (index: number) => {
    const existingTexts = messages.map((m) => m.text);
    const newMessage = regenerateSingleMessage(
      index,
      theirName.trim(),
      theirRole.trim(),
      theirCompany.trim(),
      purpose,
      yourRole.trim(),
      personalDetail.trim(),
      existingTexts,
    );
    setMessages((prev) => {
      const updated = [...prev];
      updated[index] = newMessage;
      return updated;
    });
  };

  const inputClass =
    'w-full border border-gray-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-shadow text-gray-900';
  const labelClass = 'block text-sm font-semibold text-gray-700 mb-1.5';

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-stone-100">
      <ToolHero
        title="LinkedIn Connection Request Writer"
        description="Generate personalized LinkedIn connection request messages under 300 characters. Stand out from generic 'I'd like to connect' messages and get more acceptances."
        category="LinkedIn"
      />

      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Input Form */}
        <form
          onSubmit={handleGenerate}
          className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8"
        >
          <h2 className="text-xl font-bold text-gray-900 mb-6">
            Connection Details
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {/* Their Name */}
            <div>
              <label htmlFor="theirName" className={labelClass}>
                Their Name <span className="text-red-500">*</span>
              </label>
              <input
                id="theirName"
                type="text"
                required
                value={theirName}
                onChange={(e) => setTheirName(e.target.value)}
                placeholder="e.g., Sarah Johnson"
                className={inputClass}
              />
            </div>

            {/* Their Role / Title */}
            <div>
              <label htmlFor="theirRole" className={labelClass}>
                Their Role / Title <span className="text-red-500">*</span>
              </label>
              <input
                id="theirRole"
                type="text"
                required
                value={theirRole}
                onChange={(e) => setTheirRole(e.target.value)}
                placeholder="e.g., VP of Engineering"
                className={inputClass}
              />
            </div>

            {/* Their Company */}
            <div>
              <label htmlFor="theirCompany" className={labelClass}>
                Their Company
              </label>
              <input
                id="theirCompany"
                type="text"
                value={theirCompany}
                onChange={(e) => setTheirCompany(e.target.value)}
                placeholder="e.g., Stripe"
                className={inputClass}
              />
            </div>

            {/* Purpose */}
            <div>
              <label htmlFor="purpose" className={labelClass}>
                Purpose of Connection
              </label>
              <select
                id="purpose"
                value={purpose}
                onChange={(e) => setPurpose(e.target.value as Purpose)}
                className={inputClass}
              >
                {purposes.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </div>

            {/* Your Role */}
            <div>
              <label htmlFor="yourRole" className={labelClass}>
                Your Role / Title
              </label>
              <input
                id="yourRole"
                type="text"
                value={yourRole}
                onChange={(e) => setYourRole(e.target.value)}
                placeholder="e.g., Product Manager"
                className={inputClass}
              />
            </div>

            {/* Personal Detail */}
            <div>
              <label htmlFor="personalDetail" className={labelClass}>
                Personal Detail{' '}
                <span className="font-normal text-gray-400">(optional)</span>
              </label>
              <input
                id="personalDetail"
                type="text"
                value={personalDetail}
                onChange={(e) => setPersonalDetail(e.target.value)}
                placeholder="e.g., loved your talk at SaaStr"
                className={inputClass}
              />
            </div>
          </div>

          {/* Generate Button */}
          <button
            type="submit"
            className="mt-8 w-full sm:w-auto bg-gradient-to-r from-blue-600 to-blue-500 text-white font-bold py-3 px-10 rounded-xl shadow-lg hover:from-blue-700 hover:to-blue-600 transition-all text-lg"
          >
            Generate Messages
          </button>
        </form>

        {/* Results */}
        {hasGenerated && messages.length > 0 && (
          <section className="mt-12">
            <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
              <h2 className="text-2xl font-extrabold text-gray-900">
                Your Connection Requests
              </h2>
              <button
                onClick={handleRegenerateAll}
                className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                Regenerate All
              </button>
            </div>

            <div className="space-y-4">
              {messages.map((message, idx) => {
                const charCount = message.text.length;
                const isOverLimit = charCount > CHAR_LIMIT;
                const isNearLimit = charCount > CHAR_LIMIT * 0.9 && !isOverLimit;

                return (
                  <div
                    key={message.id}
                    className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 hover:border-blue-200 hover:shadow-xl transition-all"
                  >
                    {/* Message Number Badge */}
                    <div className="flex items-center gap-3 mb-3">
                      <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-blue-100 text-blue-700 text-sm font-bold">
                        {idx + 1}
                      </span>
                      <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                        Message {idx + 1}
                      </span>
                    </div>

                    {/* Message Text */}
                    <p className="text-gray-900 leading-relaxed mb-4 break-words">
                      {message.text}
                    </p>

                    {/* Footer: char count + actions */}
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      {/* Character Count */}
                      <span
                        className={`text-xs font-semibold px-2.5 py-1 rounded-full ${
                          isOverLimit
                            ? 'bg-red-100 text-red-700'
                            : isNearLimit
                            ? 'bg-amber-100 text-amber-700'
                            : 'bg-green-100 text-green-700'
                        }`}
                      >
                        {charCount} / {CHAR_LIMIT} chars
                        {isOverLimit && ' -- over limit!'}
                      </span>

                      {/* Actions */}
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleRegenerateSingle(idx)}
                          className="inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-1.5 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                        >
                          <RefreshCw className="w-3.5 h-3.5" />
                          Regenerate
                        </button>
                        <CopyButton text={message.text} />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* Tips Section */}
        <div className="mt-12 bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">
            LinkedIn Connection Request Etiquette
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div className="flex gap-4">
              <div className="shrink-0 w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                <Target className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Always Personalize</h3>
                <p className="text-sm text-gray-600">
                  Generic connection requests get ignored. Mention something specific about the
                  person -- their role, company, a post they shared, or a shared connection. Even
                  one personal detail dramatically increases your acceptance rate.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="shrink-0 w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                <Clock className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Stay Under 300 Characters</h3>
                <p className="text-sm text-gray-600">
                  LinkedIn limits connection request notes to 300 characters. Make every word count.
                  Get straight to the point -- who you are, why you are reaching out, and what you
                  share in common. Save longer conversations for after they accept.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="shrink-0 w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                <Heart className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Lead With Value</h3>
                <p className="text-sm text-gray-600">
                  Do not start with what you need. Instead, compliment their work, mention a shared
                  interest, or reference their content. People are more likely to accept when they
                  feel appreciated rather than approached for a favor.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="shrink-0 w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Mention Mutual Connections</h3>
                <p className="text-sm text-gray-600">
                  If you share connections, mention it. Mutual contacts create instant trust and
                  credibility. A request from someone in your extended network feels much warmer
                  than one from a complete stranger.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="shrink-0 w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                <AlertCircle className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Do Not Pitch Immediately</h3>
                <p className="text-sm text-gray-600">
                  A connection request is not the place for a sales pitch or a job application.
                  Build the relationship first. Once they accept, you can follow up with a more
                  detailed message about what you are looking for.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="shrink-0 w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                <Lightbulb className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">State Your Intent Clearly</h3>
                <p className="text-sm text-gray-600">
                  Be honest about why you want to connect. Whether it is networking, learning about
                  their company, or seeking career advice, a clear purpose makes you seem genuine
                  and respectful of their time.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="shrink-0 w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                <MessageSquare className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Follow Up After They Accept</h3>
                <p className="text-sm text-gray-600">
                  Once they accept your request, send a brief thank-you message. This is your chance
                  to start a real conversation. Reference your original note and suggest a next step
                  like a quick call or coffee chat.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="shrink-0 w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                <Target className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Quality Over Quantity</h3>
                <p className="text-sm text-gray-600">
                  LinkedIn can restrict your account if you send too many connection requests that
                  get ignored. Focus on people you genuinely want to connect with and take the time
                  to craft a thoughtful note for each one.
                </p>
              </div>
            </div>
          </div>

          <p className="mt-6 pt-4 border-t border-gray-200 text-sm text-gray-500">
            <strong>Sources &amp; Further Reading:</strong>{' '}
            <a href="https://www.linkedin.com/help/linkedin/answer/a546415" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">LinkedIn Help: Sending Connection Requests</a>
            {' · '}
            <a href="https://blog.linkedin.com/" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">LinkedIn Official Blog</a>
            {' · '}
            <a href="https://hbr.org/topic/career-planning" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">Harvard Business Review: Career Planning &amp; Networking</a>
          </p>
        </div>

        {/* CTA */}
        <ToolCTA
          headline="Great connections start with a great profile."
          description="Pair your networking skills with a polished, ATS-optimized resume built in minutes with Superpower Resume."
          buttonText="Build My Resume Free"
          buttonHref="https://app.superpowerresume.com/sign-up"
        />
      </div>
    </div>
  );
}
